import React from 'react';
import { Box, Card, Columns, styled, Text } from 'bumbag';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import Loads from 'react-loads';

import CardDetailsBox from '@medipass/react-core/PaymentMethods/components/CardDetailsBox';
import HealthFundDetails from '@medipass/react-core/Transactions/components/consumer/HealthFundDetails';
import ServiceDetails from '@medipass/react-core/Transactions/components/consumer/ServiceDetails';
import ServiceItemDetails from '@medipass/react-core/Transactions/components/consumer/ServiceItemDetails';
import Totals from '@medipass/react-core/Transactions/components/consumer/Totals';
import WithErrorMessage from '@medipass/react-core/WithErrorMessage';
import WithLoadingIndicator from '@medipass/react-core/WithLoadingIndicator';
import { PAYMENT_TYPES, TRANSACTION_STATUSES } from '@medipass/utils/constants';
import { isPatientFunded as isFunderPatientFunded } from '@medipass/utils/funders';
import { mapItemsToClaimItems } from '@medipass/utils/service-items';
import sdk from '@medipass/web-sdk';

import Page from 'legacy/components/account/layout/Page';
import RefundTotals from 'legacy/components/account/transactions/RefundTotals';
import TransactionStatus from 'legacy/components/account/transactions/TransactionStatus';
import BackButton from 'legacy/components/BackButton';
import WithApprove from 'legacy/components/WithApprove';
import WithAuthentication from 'legacy/components/WithAuthentication';
import { getKeyFromQueryParams } from 'legacy/utils/router';
const StyledCard = styled(Box)`
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 0.5px solid #d3d3d3;
`;
type Props = {};
type State = {
  paymentId: string | null | undefined;
  transactionId?: string;
};
export default class Transaction extends React.Component<Props, State> {
  state = {
    paymentId: getKeyFromQueryParams('paymentId'),
    transactionId: getKeyFromQueryParams('id'),
  };
  componentDidUpdate = (prevProps: Props, prevState: State) => {
    const paymentId = getKeyFromQueryParams('paymentId');
    const transactionId = getKeyFromQueryParams('id');

    if (paymentId !== prevState.paymentId || transactionId !== prevState.transactionId) {
      this.setState({
        paymentId,
        transactionId,
      });
    }
  };
  getTransaction = async () => {
    const { transactionId } = this.state;

    if (transactionId) {
      return await sdk.transactions.getTransactionByTransactionId(transactionId);
    }

    throw new Error('Transaction ID is required');
  };
  render = () => {
    const { paymentId, transactionId } = this.state;
    return (
      <WithAuthentication>
        <Page minWidthBreakpoint="mobile" title="Transaction details">
          <Loads contextKey={`transaction-${transactionId || ''}`} loadOnMount load={this.getTransaction}>
            {/* @ts-expect-error TS(7031): Binding element 'load' implicitly has an 'any' typ... Remove this comment to see the full error message */}
            {({ load, error, response: transaction, isError, isLoading, isSuccess }) => {
              const payment = paymentId
                ? // @ts-expect-error TS(7006): Parameter 'payment' implicitly has an 'any' type.
                  _get(transaction, 'payments', []).find(payment => payment.paymentId === paymentId) || {}
                : _get(transaction, 'payments[0]', {});

              const originalPayment = _get(transaction, 'payments[0]', {});

              const claimItems = _get(transaction, 'claims[0].claimItems', []);

              const funderCode = _get(transaction, 'claims[0].funder.code');

              const hasPaidInPerson = payment.paymentType === PAYMENT_TYPES.IN_PERSON;
              const hasHealthFund = Boolean(_get(transaction, 'claims[0].healthFundAccount'));
              const isPatientFunded = isFunderPatientFunded(funderCode);
              const isRefunded = payment.paymentType === PAYMENT_TYPES.REFUND;
              const hasDiscount = _get(transaction, 'amountDiscount', 0) > 0;
              const allItems = mapItemsToClaimItems(transaction);

              let status = _get(transaction, 'status');

              if (payment.paymentType === PAYMENT_TYPES.REFUND) {
                status = TRANSACTION_STATUSES.REFUNDED;
              }

              const requiresPayment =
                status === TRANSACTION_STATUSES.PENDING && _get(transaction, 'amountBalance', 0) > 0;
              return (
                <WithLoadingIndicator isActive={isLoading}>
                  <WithErrorMessage isActive={isError} description="Unable to load your transaction." error={error}>
                    {isSuccess && (
                      <WithApprove onReject={load} isActive={requiresPayment} transaction={transaction}>
                        <Box data-testid="transaction-details">
                          <Columns minBreakpoint="mobile">
                            <Columns.Column
                              paddingTop="0px"
                              paddingBottom="0px"
                              paddingLeft="0px"
                              display="flex"
                              alignItems="center"
                            >
                              <BackButton to="/account/transactions" />
                            </Columns.Column>
                            {/* @ts-expect-error TS(2322): Type '"0"' is not assignable to type 'Flexible<Spa... Remove this comment to see the full error message */}
                            <Columns.Column paddingTop="0" paddingBottom="0" whiteSpace="nowrap" textAlign="right">
                              <Box>
                                <TransactionStatus status={status} />
                              </Box>
                              <Box>
                                <Text fontSize="150">Receipt: {transaction.transactionId}</Text>
                              </Box>
                            </Columns.Column>
                          </Columns>
                          <Card marginTop="major-1">
                            <Box>
                              <StyledCard
                                style={{
                                  borderBottom: `${
                                    !isPatientFunded || (isPatientFunded && hasDiscount)
                                      ? '0.5px solid #d3d3d3'
                                      : 'none'
                                  }`,
                                }}
                              >
                                {/* @ts-expect-error */}
                                <ServiceDetails
                                  created={transaction.created}
                                  modality={transaction.modality}
                                  practice={transaction.practice}
                                  staff={transaction.staff}
                                />
                              </StyledCard>
                              {!isPatientFunded && !isRefunded && hasHealthFund && claimItems && (
                                <StyledCard>
                                  <HealthFundDetails isFormActive={requiresPayment} transaction={transaction} />
                                </StyledCard>
                              )}
                              {isRefunded && (
                                <RefundTotals
                                  originalPayment={originalPayment}
                                  refund={payment}
                                  transactionId={_get(transaction, '_id')}
                                />
                              )}
                              {!isRefunded && (
                                <Totals
                                  funderCode={funderCode}
                                  serviceItem={allItems.length === 1 ? allItems[0] : null}
                                  title="Payment"
                                  transaction={transaction}
                                />
                              )}
                              {!_isEmpty(payment) && !hasPaidInPerson && (
                                <CardDetailsBox
                                  key={transaction._id}
                                  className="margin-top-10 margin-bottom-10"
                                  lastFour={_get(payment, 'paymentMethod.lastFour')}
                                  type={_get(payment, 'paymentMethod.cardType')}
                                >
                                  {({ type, lastFour }) => (
                                    <Box>
                                      {isRefunded ? 'Refund to' : 'Paid with'} {type} ****{lastFour}
                                    </Box>
                                  )}
                                </CardDetailsBox>
                              )}
                              {allItems.length > 1 && !isRefunded && (
                                <ServiceItemDetails
                                  funderCode={funderCode}
                                  serviceItems={allItems}
                                  transaction={transaction}
                                />
                              )}
                            </Box>
                          </Card>
                        </Box>
                      </WithApprove>
                    )}
                  </WithErrorMessage>
                </WithLoadingIndicator>
              );
            }}
          </Loads>
        </Page>
      </WithAuthentication>
    );
  };
}
